import React, {useState, useEffect} from "react";
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Grid,
    TextField,
    Button,
    Divider,
    Tab,
    Tabs,
    IconButton,
    Chip,
    Card,
    CardContent,
    Skeleton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme,
    Switch,
    FormControlLabel,
    InputAdornment,
} from "@mui/material";


import {
    Edit as EditIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    LocationOn as LocationIcon,
    Notifications as NotificationsIcon,
    CreditCard as CreditCardIcon,
    PhotoCamera as PhotoCameraIcon,
    Badge as BadgeIcon,
    Home as HomeIcon,
    SaveAlt as SaveIcon,
    CalendarMonth as CalendarIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    DocumentScanner as DocumentIcon,
    Receipt as ReceiptIcon, Logout
} from "@mui/icons-material";
import {KEY} from "../../dummyData";
import CryptoJS from "crypto-js";
import {AppsTwoTone, Devices, Person} from "@material-ui/icons";
import {Shield, SmartphoneIcon, ComputerIcon} from "lucide-react";
import PersonalInfoTab from "./components/Personalinfo";
import Documents from "./components/Documents";
import LeaseDetails from "./components/LeaseDetails";

// Sample interface for types
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Tab Panel component
function TabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`profile-tabpanel-${index}`}
            aria-labelledby={`profile-tab-${index}`}
            {...other}
            style={{padding: '24px 0'}}
        >
            {value === index && children}
        </div>
    );
}

export default function ProfilePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.down('md'));

    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);
    let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

    // State variables
    const [tenantID, setTenantID] = useState(tenantDetails.get("tenant_id"));
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [notification, setNotification] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        nationalId: "",
        emergencyContact: "",
        leaseStart: "",
        leaseEnd: "",
        property: "",
        unit: "",
        rentAmount: "",
        securityDeposit: "",
        profileImage: null
    });

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    // Notification preferences state
    const [notificationPreferences, setNotificationPreferences] = useState({
        emailNotifications: true,
        smsNotifications: true,
        paymentReminders: true,
        maintenanceUpdates: true,
        communityAnnouncements: false
    });

    // Load user profile data
    useEffect(() => {
        // Simulate API call to fetch profile data
        setTimeout(() => {
            try {
                // Normally you would get this from an API
                // For demo, we'll get it from localStorage if available
                let originalText;
                if (localStorage.getItem("tenant_details") === null) {
                    let dummyData = {
                        first_name: "John",
                        last_name: "Doe",
                        phone_number: "0712345678",
                        national_id: "123456789",
                        email_address: "john.doe@example.com",
                        house_number: "A1",
                        property_name: "Skyline Apartments",
                        rent: "1200"
                    };
                    originalText = JSON.stringify(dummyData);
                } else {
                    let details = CryptoJS.AES.decrypt(
                        localStorage.getItem("tenant_details"),
                        KEY
                    );
                    originalText = details.toString(CryptoJS.enc.Utf8);
                }

                let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

                setTenantID(tenantDetails.get("tenant_id"))

                setProfileData({
                    firstName: tenantDetails.get("first_name") || "John",
                    lastName: tenantDetails.get("last_name") || "Doe",
                    email: tenantDetails.get("email_address") || "john.doe@example.com",
                    phone: tenantDetails.get("phone_number") || "0712345678",
                    nationalId: tenantDetails.get("national_id") || "123456789",
                    emergencyContact: tenantDetails.get("emergency_contact") || "0798765432",
                    leaseStart: tenantDetails.get("lease_start") || "2023-01-01",
                    leaseEnd: tenantDetails.get("lease_end") || "2024-01-01",
                    property: tenantDetails.get("property_name") || "Skyline Apartments",
                    unit: tenantDetails.get("house_number") || "A1",
                    rentAmount: tenantDetails.get("rent") || "1200",
                    securityDeposit: tenantDetails.get("security_deposit") || "1200",
                    profileImage: null
                });

                setLoading(false);
            } catch (error) {
                console.error("Error loading profile data:", error);
                setNotification({
                    type: "error",
                    message: "Failed to load profile data. Please try again later."
                });
                setLoading(false);
            }
        }, 1500);
    }, []);

    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Toggle edit mode
    const toggleEditMode = () => {
        if (editMode) {
            // If leaving edit mode, confirm changes
            setConfirmDialog(true);
        } else {
            setEditMode(true);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setProfileData({
            ...profileData,
            [name]: value
        });
    };

    // Handle password form changes
    const handlePasswordChange = (e) => {
        const {name, value} = e.target;
        setPasswordForm({
            ...passwordForm,
            [name]: value
        });
    };

    // Save profile changes
    const saveChanges = () => {
        setLoading(true);

        // Simulate API call to save profile data
        setTimeout(() => {
            // In a real app, you would send this to your API
            console.log("Saving profile data:", profileData);

            setNotification({
                type: "success",
                message: "Profile updated successfully!"
            });

            setEditMode(false);
            setConfirmDialog(false);
            setLoading(false);

            // Clear notification after 5 seconds
            setTimeout(() => {
                setNotification(null);
            }, 5000);
        }, 1500);
    };

    // Cancel edit mode
    const cancelEdit = () => {
        // Reload original data
        setEditMode(false);
        setConfirmDialog(false);

        // In a real app, you would reload from API or cached original state
    };

    // Change password
    const changePassword = () => {
        // Validate password form
        if (!passwordForm.currentPassword) {
            setNotification({
                type: "error",
                message: "Please enter your current password"
            });
            return;
        }

        if (!passwordForm.newPassword) {
            setNotification({
                type: "error",
                message: "Please enter a new password"
            });
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setNotification({
                type: "error",
                message: "New passwords do not match"
            });
            return;
        }

        // Simulate API call to change password
        setLoading(true);

        setTimeout(() => {
            setPasswordDialog(false);
            setNotification({
                type: "success",
                message: "Password changed successfully!"
            });

            // Reset form
            setPasswordForm({
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
            });

            setLoading(false);

            // Clear notification after 5 seconds
            setTimeout(() => {
                setNotification(null);
            }, 5000);
        }, 1500);
    };

    // Get avatar initials
    const getInitials = () => {
        if (!profileData.firstName && !profileData.lastName) return "JD";

        const firstInitial = profileData.firstName ? profileData.firstName.charAt(0) : "";
        const lastInitial = profileData.lastName ? profileData.lastName.charAt(0) : "";

        return (firstInitial + lastInitial).toUpperCase();
    };

    // Handle profile image change
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();

            reader.onload = (event) => {
                setProfileData({
                    ...profileData,
                    profileImage: event.target.result
                });
            };

            reader.readAsDataURL(e.target.files[0]);
        }
    };

    // Toggle notification preference
    const toggleNotificationPreference = (key) => {
        setNotificationPreferences({
            ...notificationPreferences,
            [key]: !notificationPreferences[key]
        });
    };

    // Calculate when password was last changed
    const calculateLastPasswordChange = () => {
        // In a real app, this would come from the API
        const lastChanged = new Date();
        lastChanged.setMonth(lastChanged.getMonth() - 3); // 3 months ago

        const now = new Date();
        const diffTime = Math.abs(now - lastChanged);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 1) return "today";
        if (diffDays === 1) return "yesterday";
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "";

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format currency for display
    const formatCurrency = (amount) => {
        if (!amount) return "";

        return parseFloat(amount).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    };

    return (
        <Box sx={{
            // Fixed container that works with the Sidebar layout
            padding: { xs: 2, sm: 3, md: 4 },
            maxWidth: '1200px',
            margin: '0 auto',
            backgroundColor: '#f9fafb',
            minHeight: '100vh'
        }}>
            {/* Notification Alert */}
            {notification && (
                <Alert
                    severity={notification.type}
                    sx={{mb: 3, borderRadius: '8px'}}
                    onClose={() => setNotification(null)}
                >
                    {notification.message}
                </Alert>
            )}

            {/* Profile Header */}
            <Paper
                elevation={0}
                sx={{
                    p: {xs: 2, sm: 3},
                    mb: 3,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'white'
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    {/* Profile Image */}
                    <Grid item xs={12} sm="auto" sx={{textAlign: {xs: 'center', sm: 'left'}}}>
                        {loading ? (
                            <Skeleton variant="circular" width={100} height={100}/>
                        ) : (
                            <Box sx={{position: 'relative', display: 'inline-block'}}>
                                <Avatar
                                    src={profileData.profileImage}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        bgcolor: '#3B82F6',
                                        border: '4px solid #EBF4FF'
                                    }}
                                >
                                    {getInitials()}
                                </Avatar>

                                {editMode && (
                                    <IconButton
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1px solid #E5E7EB',
                                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {backgroundColor: '#F9FAFB'}
                                        }}
                                        component="label"
                                        size="small"
                                    >
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={handleImageChange}
                                        />
                                        <PhotoCameraIcon fontSize="small"/>
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Grid>

                    {/* Profile Info */}
                    <Grid item xs={12} sm>
                        <Box sx={{textAlign: {xs: 'center', sm: 'left'}}}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={200} height={40}/>
                                    <Skeleton variant="text" width={250} height={24}/>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1F2937',
                                            lineHeight: 1.2,
                                            mb: 0.5
                                        }}
                                    >
                                        {profileData.firstName} {profileData.lastName}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#6B7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: {xs: 'center', sm: 'flex-start'},
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <HomeIcon sx={{fontSize: '1.1rem', mr: 0.5, color: '#3B82F6'}}/>
                                        {profileData.property} • Unit {profileData.unit}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Grid>

                    {/* Edit Button */}
                    <Grid item xs={12} sm="auto">
                        <Button
                            variant={editMode ? "outlined" : "contained"}
                            color={editMode ? "success" : "primary"}
                            startIcon={editMode ? <SaveIcon/> : <EditIcon/>}
                            onClick={toggleEditMode}
                            disabled={loading}
                            sx={{
                                borderRadius: '8px',
                                boxShadow: 'none',
                                textTransform: 'none',
                                py: 1,
                                px: 2,
                                fontWeight: 500
                            }}
                        >
                            {editMode ? "Save Changes" : "Edit Profile"}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Profile Tabs Navigation */}
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile
                    aria-label="Profile information tabs"
                    sx={{
                        '& .MuiTabs-flexContainer': {
                            borderBottom: '1px solid #E5E7EB',
                        },
                        backgroundColor: 'white',
                        borderRadius: '8px 8px 0 0',
                        '& .MuiTab-root': {
                            backgroundColor: 'white'
                        }
                    }}
                >
                    <Tab
                        label="Personal Info"
                        id="profile-tab-0"
                        aria-controls="profile-tabpanel-0"
                        icon={<Person fontSize="small"/>}
                        iconPosition="start"
                        className="personal-info-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#3B82F6',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />

                    <Tab
                        label="Lease Details"
                        id="profile-tab-1"
                        aria-controls="profile-tabpanel-1"
                        icon={<HomeIcon fontSize="small"/>}
                        iconPosition="start"
                        className="lease-details-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#10B981',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />

                    <Tab
                        label="Documents"
                        id="profile-tab-4"
                        aria-controls="profile-tabpanel-4"
                        icon={<DocumentIcon fontSize="small"/>}
                        iconPosition="start"
                        className="documents-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#EC4899',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />
                </Tabs>
            </Box>

            {/* Tab Content Container */}
            <Box sx={{
                backgroundColor: 'white',
                borderRadius: '0 0 12px 12px',
                minHeight: '400px'
            }}>
                {/* Personal Info Tab */}
                <TabPanel value={tabValue} index={0}>
                    <PersonalInfoTab
                        className="personal-info-content"
                        // userData={userData} // Optional: Pass any shared data
                        onUpdate={(updatedData) => {
                            // Optional: Handle updates from the tab
                            console.log('Personal info updated:', updatedData);
                            // You could update shared state if needed
                        }}
                    />
                </TabPanel>

                {/* Lease Tab */}
                <TabPanel value={tabValue} index={1} className="lease-details-tab-panel">
                    <LeaseDetails
                        tenantId={tenantID}
                    />
                </TabPanel>

                {/* Documents Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Documents
                        tenantId={tenantID}
                    />
                </TabPanel>
            </Box>

            {/* Change Password Dialog */}
            <Dialog
                open={passwordDialog}
                onClose={() => setPasswordDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 2
                    }
                }}
            >
                <DialogTitle sx={{fontWeight: 600, color: '#1F2937'}}>
                    Change Password
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" sx={{mb: 3, color: '#6B7280'}}>
                        Please enter your current password and choose a new password.
                    </Typography>

                    <Box sx={{mt: 2}}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            name="currentPassword"
                            type={showPassword ? "text" : "password"}
                            value={passwordForm.currentPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                            margin="normal"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="New Password"
                            name="newPassword"
                            type={showPassword ? "text" : "password"}
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                            margin="normal"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            name="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                            variant="outlined"
                            margin="normal"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                }
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button
                        variant="outlined"
                        onClick={() => setPasswordDialog(false)}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={changePassword}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            boxShadow: 'none'
                        }}
                    >
                        Change Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Changes Dialog */}
            <Dialog
                open={confirmDialog}
                onClose={() => setConfirmDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px'
                    }
                }}
            >
                <DialogTitle sx={{fontWeight: 600, color: '#1F2937'}}>
                    Save Changes
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to save these changes to your profile?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{p: 2}}>
                    <Button
                        variant="outlined"
                        onClick={cancelEdit}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={saveChanges}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            boxShadow: 'none'
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};